/**
 * Own integer physics of Tacada Limpa (docs/PHYSICS.md): top-down ball on felt, fixed 16 ms step, linear friction,
 * walls at 0°, 90° and 45° with exact reflections (sign swaps, no trigonometry), sand (more friction), ramps (a constant
 * drift while the ball rolls) and gates that slide on the match clock. Everything is an integer, so the browser and Node
 * produce the same final position for the same putt (docs/PROTOCOL.md §1).
 *
 * Units: "sub" = 1/16 of a table pixel. The table is 640 x 1000 px.
 */
import { ANGLES, COS, SIN, TRIG_ONE } from './trig'

export const SUB = 16
export const TABLE_W = 640 * SUB
export const TABLE_H = 1000 * SUB
export const BALL_R = 12 * SUB
export const CUP_R = 20 * SUB
export const STEP_MS = 16
/** Speed (sub per step) of a full-strength putt. */
export const VMAX = 640
export const FRICTION = 6
export const SAND_FRICTION = 24
/** A ball faster than this rolls over the cup (lip out). */
export const CUP_MAX_SPEED = 380
/** Speed kept after a wall (percent). */
export const BOUNCE_KEEP = 85
export const MAX_STEPS = 1500
const SUBSTEP = 96
/** 1000 * sqrt(2), for the distance to a 45° line without a square root. */
const ROOT2_1000 = 1414

export interface Pt { x: number; y: number }
export interface Rect { x: number; y: number; w: number; h: number }
/** 45° wall. '/' is the line x + y = c (extent: x - y in lo..hi); '\' is x - y = c (extent: x + y in lo..hi). */
export interface Diag { kind: '/' | '\\'; c: number; lo: number; hi: number }
export interface Zone extends Rect { kind: 'sand' | 'ramp'; dx?: number; dy?: number }
/** A wall that slides back and forth along one axis on the match clock. */
export interface Gate extends Rect { axis: 'x' | 'y'; amp: number; periodMs: number }

export interface Table { walls: Rect[]; diags: Diag[]; zones: Zone[]; gates: Gate[] }
export interface Course extends Table { tee: Pt; cup: Pt; gatePhaseMs: number }

/** Triangle wave in -amp..amp, integer. */
export function gateOffset(g: Gate, tMs: number, phaseMs: number): number {
  const P = g.periodMs
  const u = (((tMs + phaseMs) % P) + P) % P
  return u < P / 2 ? Math.trunc((g.amp * (4 * u - P)) / P) : Math.trunc((g.amp * (3 * P - 4 * u)) / P)
}

/** The gate's rectangle at time t. */
export function gateAt(g: Gate, tMs: number, phaseMs: number): Rect {
  const o = gateOffset(g, tMs, phaseMs)
  return g.axis === 'x' ? { x: g.x + o, y: g.y, w: g.w, h: g.h } : { x: g.x, y: g.y + o, w: g.w, h: g.h }
}

/** Integer square root (floor), exact for the sizes used here. */
export function isqrt(n: number): number {
  if (n <= 0) return 0
  let x = Math.floor(Math.sqrt(n))
  while (x * x > n) x--
  while ((x + 1) * (x + 1) <= n) x++
  return x
}

const inRect = (r: Rect, x: number, y: number, pad: number) => x > r.x - pad && x < r.x + r.w + pad && y > r.y - pad && y < r.y + r.h + pad

export interface PuttResult {
  stop: Pt
  holed: boolean
  steps: number
  bounces: number
  /** Ball position at every step (only when asked). */
  path?: Pt[]
}

/**
 * Simulates one putt from `start`: angle in 1/1000 of a turn (0..999), strength 1..1000, struck at match time t0.
 * Pure: the same arguments always give the same result.
 */
export function simulate(c: Course, start: Pt, angle: number, strength: number, t0Ms: number, record = false): PuttResult {
  const a = ((Math.trunc(angle) % ANGLES) + ANGLES) % ANGLES
  let dx = COS[a], dy = SIN[a]
  let sp = Math.trunc((Math.max(1, Math.min(1000, Math.trunc(strength))) * VMAX) / 1000)
  let x = start.x, y = start.y
  let bounces = 0
  const path: Pt[] | undefined = record ? [{ x, y }] : undefined
  const R = BALL_R

  /** Moves the ball by (mx, my); on contact restores the previous position and reflects. Returns true on contact. */
  const move = (mx: number, my: number, t: number): boolean => {
    const px = x, py = y
    x += mx; y += my
    let hitX = false, hitY = false
    // table border
    if (x < R || x > TABLE_W - R) hitX = true
    if (y < R || y > TABLE_H - R) hitY = true
    const rects = c.gates.length ? [...c.walls, ...c.gates.map((g) => gateAt(g, t, c.gatePhaseMs))] : c.walls
    for (const r of rects) {
      if (!inRect(r, x, y, R)) continue
      const outX = px <= r.x - R || px >= r.x + r.w + R
      const outY = py <= r.y - R || py >= r.y + r.h + R
      if (outX) hitX = true
      if (outY) hitY = true
      if (!outX && !outY) { hitX = true; hitY = true } // started inside a moving gate: push straight back
    }
    if (hitX || hitY) {
      if (hitX) dx = -dx
      if (hitY) dy = -dy
      x = px; y = py
      return true
    }
    for (const d of c.diags) {
      const s = d.kind === '/' ? x + y - d.c : x - y - d.c
      const along = d.kind === '/' ? x - y : x + y
      if (Math.abs(s) * 1000 >= R * ROOT2_1000 || along < d.lo - R || along > d.hi + R) continue
      const ndx = d.kind === '/' ? -dy : dy, ndy = d.kind === '/' ? -dx : dx
      dx = ndx; dy = ndy
      x = px; y = py
      return true
    }
    return false
  }

  let steps = 0
  for (; steps < MAX_STEPS && sp > 0; steps++) {
    const t = t0Ms + steps * STEP_MS
    let friction = FRICTION, rdx = 0, rdy = 0
    for (const z of c.zones) {
      if (!inRect(z, x, y, 0)) continue
      if (z.kind === 'sand') friction = SAND_FRICTION
      else { rdx += z.dx ?? 0; rdy += z.dy ?? 0 }
    }
    const n = Math.max(1, Math.ceil(sp / SUBSTEP))
    let left = sp
    for (let i = 0; i < n; i++) {
      const m = i === n - 1 ? left : Math.trunc(sp / n)
      left -= m
      if (move(Math.trunc((m * dx) / TRIG_ONE), Math.trunc((m * dy) / TRIG_ONE), t)) { bounces++; sp = Math.trunc((sp * BOUNCE_KEEP) / 100); break }
    }
    if (rdx || rdy) move(rdx, rdy, t)
    const ex = x - c.cup.x, ey = y - c.cup.y
    if (ex * ex + ey * ey < CUP_R * CUP_R && sp <= CUP_MAX_SPEED) {
      x = c.cup.x; y = c.cup.y
      path?.push({ x, y })
      return { stop: { x, y }, holed: true, steps: steps + 1, bounces, path }
    }
    sp = Math.max(0, sp - friction)
    path?.push({ x, y })
  }
  return { stop: { x, y }, holed: false, steps, bounces, path }
}

/** Distance (table px, floor) between two points. */
export const distPx = (a: Pt, b: Pt) => Math.floor(isqrt((a.x - b.x) * (a.x - b.x) + (a.y - b.y) * (a.y - b.y)) / SUB)
